import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  message,
  Row,
  Table,
  Tooltip,
  Spin,
} from 'antd';
import { useEffect, useState, useRef } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { fetchProformaById } from '../../slices/proforma';
import dayjs from 'dayjs';
import PrintProforma from '../Authentication/PrintProforma';
import html2pdf from 'html2pdf.js';
import { LoadingOutlined } from '@ant-design/icons';

const ViewProforma = () => {
  const { id } = useParams();
  const { user: currentUser } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const [proformaData, setProformaData] = useState(null);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(10);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const printRef = useRef();
  const columns = [
    {
      title: 'Sr.No',
      dataIndex: 'SrNo',
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Section Number',
      dataIndex: 'profile_no',
      key: 'profile_no',
      sorter: false,
      render: profile_no => {
        return <span>{profile_no?.die_number || 'N/A'}</span>;
      },
    },
    {
      title: 'Customer Reference No.',
      dataIndex: 'customer_reference_no',
      render: customer_reference_no => {
        return <span>{customer_reference_no || 'N/A'}</span>;
      },
    },
    {
      title: 'Profile Image',
      dataIndex: 'profile_no',
      key: 'profile_no',
      render: profile_no => {
        return profile_no?.die_diagram ? (
          <img src={profile_no.die_diagram} alt="Die Diagram" style={{ maxWidth: '60px', maxHeight: '60px', objectFit: 'contain' }} />
        ) : (
          <span>N/A</span>
        );
      }
    },
    {
      title: 'Profile Description',
      dataIndex: 'profile_no',
      key: 'profile_no',
      render: (text, record) => {
        const dimensions = [
          record?.profile_no?.dimension1,
          record?.profile_no?.dimension2,
          record?.profile_no?.dimension3,
          record?.profile_no?.dimension4,
        ]
          .filter(dim => dim !== null && dim !== '' && dim !== undefined && dim !== '0.00')
          .map(dim => Number(dim).toFixed(2))
          .join('MM X ');

        return <span>{dimensions || 'N/A'}</span>;
      },
    },
    {
      title: 'Alloy/Temp',
      dataIndex: 'alloy_temp',
      key: 'alloy',
      render: (_, record) => {
        const alloyName = record?.alloy?.alloy_code || 'N/A';
        const alloyCode = record?.alloy?.standard_name || 'N/A';
        const temperName = record?.temper?.temper_code_new || 'N/A';
        const temperCode = record?.temper?.temper_code_old || 'N/A';

        return (
          <span>
            {alloyName} ({alloyCode}) / {temperName} ({temperCode})
          </span>
        );
      },
    },
    {
      title: 'Surface finish',
      dataIndex: 'jobworks',
      key: 'jobworks',
      sorter: false,
      render: jobworks => {
        if (Array.isArray(jobworks) && jobworks.length > 0) {
          return <span>{jobworks.join(', ')}</span>;  // ✅ show as comma-separated string
        }
        return <span>N/A</span>;
      },
    },
    {
      title: 'Length(mm)',
      dataIndex: 'length',
      key: 'length',
      sorter: false,
      render: length => {
        return <span>{parseInt(length) || 'N/A'}</span>;
      },
    },
    {
      title: 'Pieces',
      dataIndex: 'pieces',
      key: 'pieces',
      sorter: false,
      render: pieces => {
        return <span>{pieces || 'N/A'}</span>;
      },
    },
    {
      title: 'Net Weight(kg)',
      dataIndex: 'net_weight',
      key: 'net_weight',
      sorter: false,
      render: net_weight => {
        return <span>{net_weight || 'N/A'}</span>;
      },
    },

    {
      title: 'Nalco Rate',
      dataIndex: 'nalco_rate',
      key: 'nalco_rate',
      sorter: false,
      render: nalco_rate => {
        return <span>{nalco_rate || 'N/A'}</span>;
      },
    },
    {
      title: 'Packed Weight (kg)',
      dataIndex: 'packed_weight',
      key: 'packed_weight',
      sorter: false,
      render: packed_weight => {
        return <span>{packed_weight || 'N/A'}</span>;
      },
    },
    {
      title: 'Dispatch Qty',
      dataIndex: 'dispatch_qty',
      sorter: false,
      key: 'dispatch_qty',
       render: (value) => value ?? 0,
    },
    {
      title: 'Total Basic Amount',
      dataIndex: 'total_basic_amount',
      key: 'total_basic_amount',
      render: total_basic_amount => {
        return <span>{!isNaN(total_basic_amount) ? Number(total_basic_amount).toFixed(2) : 'N/A'}</span>;
      },
    },
    {
      title: 'IGST (18%)',
       dataIndex: 'gst_amount',
      key: 'gst_amount',
      render: gst_amount => {
        return <span>{!isNaN(gst_amount) ? Number(gst_amount).toFixed(2) : 'N/A'}</span>;
      },
    },
    {
      title: 'Total Amount (Rs.)',
       dataIndex: 'total_amount',
      key: 'total_amount',
      render: total_amount => {
        return <span>{!isNaN(total_amount) ? Number(total_amount).toFixed(2) : 'N/A'}</span>;
      },
    },
  ];

  useEffect(() => {
    const fetchProformaDetails = async () => {
      try {
        dispatch(fetchProformaById({ id: id }))
          .unwrap()
          .then(response => {
            if (response?.success) {
              setProformaData(response?.data);
            }
          })
          .catch(error => {
            console.error('View Quotation Failed:', error);
          });
      } catch (error) {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error);
        } else if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An unknown error occurred.');
        }
      }
    };

    fetchProformaDetails();
  }, [dispatch, id]);

  

  const loadImageAsBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const cacheBustUrl = `${url}${url.includes('?') ? '&' : '?'}nocache=${new Date().getTime()}`;

      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = cacheBustUrl;
    });
  };

  const handlePdfDownload = () => {
    const element = printRef.current;
    if (!element) return;

    setLoadingPDF(true);
    const clone = element.cloneNode(true);
    clone.querySelectorAll('.no-print').forEach(el => el.remove());
    const logo = clone.querySelector('img.company-logo');
    if (logo) {
      const logoWrapper = document.createElement('div');
      logoWrapper.style.backgroundColor = 'white';
      logoWrapper.style.display = 'inline-block';
      logoWrapper.style.padding = '6px';
      logoWrapper.style.borderRadius = '4px';
      logoWrapper.style.textAlign = 'center';

      logo.parentNode.insertBefore(logoWrapper, logo);
      logoWrapper.appendChild(logo);
    }
    const imageElements = Array.from(clone.querySelectorAll('img')).filter(img => {
      const src = img.getAttribute('src') || '';
      return !img.classList.contains('company-logo') && !src.includes('logo.png');
    });

    const style = document.createElement('style');
    style.innerHTML = `
    * {
        box-sizing: border-box !important;
        margin: 0 !important;
        padding: 1px !important;
        color: #000000 !important;
        font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
      }
      @page {
        size: A4 portrait;
        margin: 10mm !important;
      }
      body {
        font-size: 9px !important;
        margin: 0 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      .invoice-container {
        width: 794px !important;
        min-height: 1000px !important;
        position: relative !important;
        padding-bottom: 100px !important;
        margin: 0 auto !important;
      }
      .main-heading {
        font-size: 15px !important;
        text-align: center !important;
        width: 100% !important;
      }
      .page {
        page-break-after: always !important;
        position: relative !important;
        box-sizing: border-box !important;
        overflow: visible !important;
        width: 794px !important;
      }
      .header-text {
        font-size: 10px !important;
        line-height: 1.2 !important;
        text-align: left !important;
      }
      .page-content {
        width: 100% !important;
        overflow: visible !important;
        font-size: 10px !important;
      }
      .signature-section {
        display: flex;
        justify-content: space-around;
        margin-top:40px !important;
      }
      .ant-row {
        display: flex !important;
        flex-wrap: wrap !important;
        margin-bottom: 5px !important;
      }
      .ant-col {
        box-sizing: border-box !important;
        padding: 1px !important;
      }
      h1, h2, h3, h4, h5 {
        color: rgb(2, 15, 28) !important;
        font-size: 12px !important;
        margin-bottom: 3px !important;
        margin-top: 0 !important;
      }
      .header-section {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding-bottom: 10px;
        margin-bottom: 20px;
      }
      .main-table {
        width: 794px !important;
        font-size: 9px !important;
        table-layout: fixed !important;
        margin: 5px 0 !important;
        border-collapse: collapse !important;
      }
      .main-table-container {
        overflow: visible !important;
        border: 1px solid #dee2e6 !important;
        padding: 0px !important;
        width: 794px !important;
      }
      .main-table-wrapper {
        padding: 0 !important;
        overflow: visible !important;
        width: 794px !important;
      }
      .main-table-thead > tr > th,
      .main-table-tbody > tr > td {
        padding: 2px 2px !important;
        font-size: 8px !important;
        background-color: white !important;
        border: 1px solid #dee2e6 !important;
        white-space: normal !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        word-break: break-word !important;
        line-height: 1.1 !important;
        height: auto !important;
        min-height: 18px !important;
      }
      .main-table-thead > tr > th {
        background: #fff !important;
        padding: 2px !important;
        font-weight: bold !important;
      }
      .main-table-wrapper .main-table-summary > tr > td {
        border: 1px solid #dee2e6 !important;
        padding: 1px 1px !important;
        font-size: 8px !important;
      }
      .summary-cell-center {
        text-align: center !important;
      }
      .summary-cell-right {
        text-align: right !important;
        padding-right: 3px !important;
      }
      .ant-divider {
        margin: 5px 0 !important;
        border-top: 1px solid #d9d9d9 !important;
      }
      .no-print {
        display: none !important;
      }
      .info-section {
        display: flex;
        justify-content: end;
        margin-bottom: 20px;
      }
      .info-left {
        flex: 1;
        max-width: 60%;
      }
      .info-right {
        flex: 1;
        text-align: right;
        max-width: 35%;
      }
      .info-table {
        border-collapse: collapse;
        width: 100%;
        font-size: 12px;
      }
      .info-table td {
        border: 1px solid #dee2e6;
        padding: 4px 8px;
        vertical-align: top;
      }
      .info-label {
        font-weight: bold;
        white-space: nowrap;
      }
      .main-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px !important;
        margin-bottom: 20px;
      }
      .main-table th,
      .main-table td {
        border: 1px solid #dee2e6;
        padding: 6px 4px;
        text-align: center;
        vertical-align: middle;
        font-size: 9px;
      }
      .main-table th {
        background: transparent;
        color: black;
        font-weight: bold;
        font-size: 9px;
      }
      .main-table .section-image {
        width: 60px;
        height: 60px;
        object-fit: contain;
      }
      .total-row {
        font-weight: bold;
      }
      .signature-box {
        text-align: center;
        font-weight: bold;
        font-size: 12px;
      }
      .invoice-title {
        font-size: 22px !important;
        font-weight: 300;
        color: #12406a;
        margin: 25px !important;
      }  
      .logo-section {
        flex: 0 0 auto;
      }
      .logo {
        width: 80px;
        height: auto;
      }
      .title-section {
        flex: 0 0 auto;      
        text-align: right;
      }
      .copy-label {
        float: right;
        font-size: 14px;
        font-weight: normal;
      }
      .new-info {
        margin-top: 20px !important;
      }
      .address-section {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;
      }
      .address-box {
        flex: 1;
        border: 1px solid #ccc;
      }
      .address-header {
        background: #12406a !important;
        color: white !important;
        padding: 8px 12px;
        font-weight: bold;
        font-size: 12px !important;
      }
      .address-content {
        padding: 12px;
        font-size: 10px;
        line-height: 1.5;
        min-height: 80px;
      }
      .footer-section {
        display: flex;
        gap: 20px;
        margin-bottom: 30px;
      }
      .bank-box {
        flex: 1;
        border: 1px solid #ccc;
      }
      .bank-header {
        background: #12406a;
        color: white !important;
        padding: 8px 12px;
        font-weight: bold;
        font-size: 13px;
      }
      .bank-content {
        padding: 2px;
        font-size: 10px !important;
        line-height: 1.6;
      }
      .print-button {
        display: none;
      }
      .no-print {
        display: none !important;
      }
      .terms-fullpage {
        page-break-before: always;
        break-before: page;
        width: 100%;
        max-width: 100%;
        background: #fff;
        display: flex;
        box-sizing: border-box;
        flex-direction: column;
      }
      .terms-content {
        font-size: 8px;
        line-height: 0.8;
        color: #222;
        flex: 1;
        margin: 0;
        padding: 2px !important;
      }`;
    clone.appendChild(style);
    const base64ConversionPromises = imageElements.map(img => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:') || img.classList.contains('company-logo')) {
        return Promise.resolve();
      }

      return loadImageAsBase64(src)
        .then(base64 => {
          img.setAttribute('src', base64);
        })
        .catch(err => {
          console.warn('Failed to load base64 image:', src, err);
        });
    });
    const logoImage = clone.querySelector('img.company-logo');
    if (logoImage && !logoImage.src.startsWith('data:')) {
      base64ConversionPromises.push(
        loadImageAsBase64(logoImage.src)
          .then(base64 => {
            logoImage.setAttribute('src', base64);
          })
          .catch(err => {
            console.warn('Failed to load base64 logo:', logoImage.src, err);
          })
      );
    }
    Promise.all(base64ConversionPromises)
      .then(() => {
        setTimeout(() => {
          const opt = {
            margin: 0,
            filename: `Proforma_${proformaData?.proforma_no || 'N/A'}.pdf`,
            image: { type: 'png', quality: 1 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              logging: true,
              imageTimeout: 15000,
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
          };

          html2pdf().set(opt).from(clone).save()
            .then(() => setLoadingPDF(false))
            .catch(err => {
              console.error("PDF generation error:", err);
              setLoadingPDF(false);
            });
        }, 500);
      })
      .catch(err => {
        console.error("Error converting images to base64:", err);
        setLoadingPDF(false);
      });
  };


  const dataSource = proformaData?.proforma_details.map((item, index) => ({
    ...item,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  return (
    <>
      {loadingPDF && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48, color: '#12406a' }} spin />}
            tip="Generating PDF..."
          />
        </div>
      )}
      <div>
        <div className="sticyaction">
          <Row className="pagenamerow mb-0" justify="space-between" align="middle">
            <Col>
              <h2 style={{ margin: 0 }}>View Proforma </h2>
              <div className="bredcrumbwrp">
                <Link to="/proforma-list" className="back">
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    { title: <Link to="/proforma-list">Proforma List</Link> },
                    { title: 'View Proforma List' },
                  ]}
                />
              </div>
            </Col>
            <Col>
              <Button type="primary"

                style={{
                  marginBottom: '20px',
                  float: 'center',
                  marginRight: '10px',
                }}
                icon={<EditOutlined />}
                onClick={() => navigate(`/edit-proforma/${proformaData?.id}`)}
              >
                Edit Proforma
              </Button>
              {currentUser.permissions.permissions.proforma.includes(
                'view_proforma',
              ) && (
                  <>
                    <Tooltip
                      placement="top"
                      title={'Download PDF'}
                    >
                      <Button
                        type="primary"
                        icon={loadingPDF ? <LoadingOutlined /> : <DownloadOutlined />}
                        style={{ marginBottom: '20px', float: 'right' }}
                        onClick={handlePdfDownload}
                        loading={loadingPDF}
                      >
                        {loadingPDF ? 'Generating PDF...' : 'Download PDF'}
                      </Button>
                    </Tooltip>
                    <div style={{ display: 'none' }}>
                      <div ref={printRef} style={{ width: '800px' }}>
                        <PrintProforma />
                      </div>
                    </div>
                  </>
                )}
            </Col>
          </Row>
        </div>
        <div className='formwrapper'>
          <Row gutter={[16, 16]} className="viewform">
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Proforma Date</label>
              {proformaData?.proforma_date
                ? dayjs(proformaData?.proforma_date).format('DD/MM/YYYY')
                : 'N/A'}
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Proforma No.</label>
              <div className="customvalue">
                {proformaData?.proforma_no || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">WorkOrder No.</label>
              <div className="customvalue">
                {proformaData?.workorder_no || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Customer Name</label>
              <div className="customvalue">
                {proformaData?.customer_name || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Mode Of Packing</label>
              <div className="customvalue">
               {proformaData?.packing_mode_details?.length
                  ? proformaData.packing_mode_details.map(item => item.name).join(', ')
                  : 'N/A'}
              </div>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Freight Charges</label>
              <div className="customvalue">
                {proformaData?.freight_charges || 'N/A'}{' '}
              </div>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Adavance amount</label>
              <div className="customvalue">
                {proformaData?.advance_amount ? Number(proformaData?.advance_amount).toLocaleString('en-IN') : 'N/A'}{' '}
              </div>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Delivery Schedule</label>
              <div className="customvalue">
                {proformaData?.delivery_schedule || 'N/A'}{' '}
              </div>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Weight Range</label>
              <div className="customvalue">
                {proformaData?.weight_range || 'N/A'}{' '}
              </div>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Remarks</label>
              <div className="customvalue">
                {proformaData?.remarks || 'N/A'}{' '}
              </div>
            </Col>
          </Row>

          <Divider />
          <Table className='px-0'
            dataSource={dataSource}
            columns={columns}
            bordered
            size="small"
            showHeader
            pagination={false}
          /></div>
      </div>
    </>
  );
};

export default ViewProforma;
